import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchIncidentTypeCountsThunk,
} from '../features/reports/reportThunks';

const styles = `
 /* Theft report dashboard specific styling */
  .theft-dashboard-wrapper {
  padding: 0px;
  width:48%;
  }
  
  /* Container for the entire dashboard */
  .theft-dashboard-container {
    width: 100%;
    background-color: #f0f0f0;
    border-radius: 20px;
    padding: 15px;
    margin: 0 ;
    box-sizing:border-box;
  }
  
  /* Header section with dropdown and filters */
  .theft-dashboard-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }
  
  .theft-dropdown-menu {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 24px;
    color: #333;
  }
  
  .theft-filters-button {
    background-color: #fff;
    border-radius: 30px;
    padding: 10px 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-size: 16px;
    font-weight: 500;
    color: #333;
    box-shadow: 0 2px 5px rgba(0,0,0,0.05);
  }
  
  /* Statistics item row styling */
  .theft-stats-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  
  .theft-stats-item {
    background-color: #fff;
    border-radius: 15px;
    padding: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: 0 2px 5px rgba(0,0,0,0.05);
  }
  
  .theft-stats-icon-name {
    display: flex;
    align-items: center;
  }
  
  .theft-stats-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    width: 26px;
    height: 26px;
  }
  
  .theft-stats-name {
    font-size: 13px;
    font-weight: 400;
    color: #777;
    margin-left: 10px;
  }
  
  .theft-stats-value {
    font-size: 15px;
    font-weight: 600;
    color: #000;
  }
  
  .theft-stats-percentage {
    background-color: #f0f0f0;
    border-radius: 20px;
    padding: 5px 12px;
    font-size: 13px;
    font-weight: 500;
    color: #333;
  }
  
  /* Icon specific colors */
  .theft-icon-prevented {
    color: #4CAF50; /* Green */
  }
  
  .theft-icon-reported {
    color: #F44336; /* Red */
  }
  
  .theft-icon-incident {
    color: #2196F3; /* Blue */
  }
  
  .theft-icon-pending {
    color: #9C27B0; /* Purple */
  }
  
  .theft-icon-incentive {
    color: #FF9800; /* Orange */
  }
  
  /* Skeleton Loading Styles */
  .theft-stats-skeleton-item {
    background-color: #fff;
    border-radius: 15px;
    padding: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: 0 2px 5px rgba(0,0,0,0.05);
  }
  
  .theft-skeleton-icon {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background-color: #e0e0e0;
    animation: pulse 1.5s infinite ease-in-out;
  }
  
  .theft-skeleton-name {
    width: 100px;
    height: 13px;
    margin-left: 10px;
    background-color: #e0e0e0;
    border-radius: 4px;
    animation: pulse 1.5s infinite ease-in-out;
  }
  
  .theft-skeleton-value {
    width: 30px;
    height: 15px;
    background-color: #e0e0e0;
    border-radius: 4px;
    animation: pulse 1.5s infinite ease-in-out;
  }
  
  .theft-skeleton-percentage {
    width: 40px;
    height: 13px;
    background-color: #e0e0e0;
    border-radius: 20px;
    animation: pulse 1.5s infinite ease-in-out;
  }
  
  @keyframes pulse {
    0% {
      opacity: 0.6;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0.6;
    }
  }
`;

// map your incident_type keys → display
const INCIDENT_MAP = {
  theftPrevented:   { name: 'Theft Prevented', icon: '🛡️', iconClass: 'theft-icon-prevented' },
  theftReported:    { name: 'Theft Reported', icon: '⚠️', iconClass: 'theft-icon-reported' },
  incidentReported: { name: 'Incident Reported', icon: '📝', iconClass: 'theft-icon-incident' },
  customerDenied:   { name: 'Customer Denied', icon: '🚫', iconClass: 'theft-icon-pending' },
  cashierSuspicious:{ name: 'Cashier Suspicious', icon: '👀', iconClass: 'theft-icon-incentive' },
};

// Skeleton Item Component for loading state
const SkeletonStatItem = () => (
  <div className="theft-stats-skeleton-item">
    <div className="theft-stats-icon-name">
      <div className="theft-skeleton-icon"></div>
      <div className="theft-skeleton-name"></div>
    </div>
    <div className="theft-skeleton-value"></div>
    <div className="theft-skeleton-percentage"></div>
  </div>
);

// Menu and Filter Icons
const MenuIcon = () => (
  <svg 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M3 12H21M3 6H21M3 18H21" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronIcon = () => (
  <svg 
    width="20" 
    height="20" 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M6 9L12 15L18 9" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

const FilterIcon = () => (
  <svg 
    width="20" 
    height="20" 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M4 6H20M4 12H16M4 18H12" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

const IncentiveInfo = ({ storeId }) => {
  const dispatch = useDispatch();
  const { counts, loading } = useSelector(state => state.reports.incidentTypeCounts);

  useEffect(() => {
    // If storeId is provided, pass it to fetch store-specific data
    const params = storeId ? { store_id: storeId } : {};
    dispatch(fetchIncidentTypeCountsThunk(params));
  }, [dispatch, storeId]);

  const total = Object.values(counts || {}).reduce((sum, v) => sum + v, 0);

  return (
    <div className="theft-dashboard-wrapper">
      <style>{styles}</style>
      <div className="theft-dashboard-container">
        <div className="theft-dashboard-header">
          <div className="theft-dropdown-menu">
            <MenuIcon />
            <ChevronIcon />
          </div>
          <div className="theft-filters-button">
            Filters 
            <FilterIcon />
          </div>
        </div>

        <div className="theft-stats-list">
          {loading
            ? // Skeleton loading UI - show skeleton items for each type in INCIDENT_MAP
              Object.keys(INCIDENT_MAP).map((key, idx) => (
                <SkeletonStatItem key={idx} />
              ))
            : Object.entries(INCIDENT_MAP).map(([key, info]) => {
                const value = counts?.[key] || 0;
                const pct = total ? `${Math.round((value/total)*100)}%` : '0%';
                return (
                  <div key={key} className="theft-stats-item">
                    <div className="theft-stats-icon-name">
                      <div className={`theft-stats-icon ${info.iconClass}`}>
                        {info.icon}
                      </div>
                      <div className="theft-stats-name">{info.name}</div>
                    </div>
                    <div className="theft-stats-value">{value}</div>
                    <div className="theft-stats-percentage">{pct}</div>
                  </div>
                );
            })}
        </div>
      </div>
    </div>
  );
};

export default IncentiveInfo;